// src/DownloadDocsOpenApiButton.jsx
import React, { useState } from "react";

/** tiny helpers */
const slug = (s) =>
    String(s || "")
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^\w.-]/g, "");

function download(obj, filename) {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function capitalizeFirstLetter(str) {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}

/** Encode each path segment safely (handles spaces, #, ?, etc.) */
function encodePath(relPath) {
    return String(relPath).split("/").map(encodeURIComponent).join("/");
}

/** Resolve a file URL relative to the manifest URL (no hardcoding) */
function resolveRelative(manifestUrl, relFile) {
    const base = new URL(manifestUrl, window.location.origin);
    // Strip trailing "index.json" to get the folder URL
    const folder = base.pathname.replace(/\/index\.json$/i, "").replace(/\/+$/, "");
    return `${folder}/${encodePath(relFile)}`;
}

/** Build an OpenAPI spec that shows each Markdown as a "pseudo endpoint" under /_docs/* */
async function buildDocsOpenApi(manifestUrl, path) {
    const bust = `t=${Date.now()}`;

    // 1) Load manifest
    const res = await fetch(`${manifestUrl}${manifestUrl.includes("?") ? "&" : "?"}${bust}`, {
        cache: "no-store",
    });
    if (!res.ok) throw new Error(`Failed to load manifest ${manifestUrl} (${res.status})`);
    const { files } = await res.json();

    const paths = {};
    for (const rel of files || []) {
        const fileUrl = `${resolveRelative(manifestUrl, rel)}?${bust}`;
        const r = await fetch(fileUrl, { cache: "no-store" });
        if (!r.ok) throw new Error(`Failed to load ${fileUrl} (${r.status})`);
        const md = await r.text();

        if (/<!doctype html/i.test(md) || /<html[\s>]/i.test(md)) {
            throw new Error(`Expected Markdown but received HTML for "${rel}". ` + `Check the file path and ensure the file is publicly served.`);
        }

        const titleMatch = md.match(/^\s*#\s+(.+?)\s*$/m);
        const title = titleMatch ? titleMatch[1].trim() : rel.replace(/\.md$/i, "");
        const route = `/_docs/${slug(rel.replace(/\.md$/i, ""))}`;

        paths[route] = {
            get: {
                tags: [capitalizeFirstLetter(path)],
                summary: title,
                description: md,
            },
        };
    }

    // 5) Final spec
    const spec = {
        openapi: "3.0.3",
        info: { title: "DOCUMENTATION", version: "1.0.0" },
        tags: [{ name: capitalizeFirstLetter(path), description: "" }],
        paths,
    };

    return spec;
}

export default function DownloadDocsOpenApiButton({ path }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const onClick = async () => {
        try {
            setBusy(true);
            setErr("");
            const manifestUrl = `/${encodePath(path)}/index.json`; // encode path just in case
            const spec = await buildDocsOpenApi(manifestUrl, path);
            download(spec, `${slug(path)}.json`);
        } catch (e) {
            setErr(String(e?.message || e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <button onClick={onClick} disabled={busy}>
                {busy ? "Building…" : `Download docs - ${path}`}
            </button>
            {err && <pre style={{ color: "crimson", whiteSpace: "pre-wrap", marginTop: 8 }}>{err}</pre>}
        </div>
    );
}
